import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import { getPhotos, API_BASE_URL } from '../services/api';
import { formatDateDisplay, getRatingColor, getRatingLabel, isPhotoDraft } from '../utils/dateUtils';

const GalleryPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPhotos();
      setPhotos(data);
    } catch (err) {
      setError('Failed to load photos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getPhotoUrl = (photo) => {
    if (!photo.file_path) return null;
    if (photo.file_path.startsWith('http')) {
      return photo.file_path;
    }
    return `${API_BASE_URL}${photo.file_path}`;
  };

  const galleryPhotos = photos.filter((photo) => photo.file_path && !isPhotoDraft(photo));

  const handlePhotoClick = (photo) => {
    // Date is already in YYYY-MM-DD string format
    navigate(`/date/${photo.date}`);
  };

  // Group photos by month
  const groupPhotosByMonth = (photos) => {
    const grouped = {};
    photos.forEach((photo) => {
      const [year, month] = photo.date.split('-');
      const key = `${year}-${month}`;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(photo);
    });
    return grouped;
  };

  // Get month name from YYYY-MM format
  const getMonthName = (yearMonth) => {
    const [year, month] = yearMonth.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Format date for small caption (just date, no day name)
  const formatDateShort = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day, 12, 0, 0);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const groupedPhotos = groupPhotosByMonth(galleryPhotos);
  const sortedMonths = Object.keys(groupedPhotos).sort().reverse(); // Most recent first

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth="lg" 
      sx={{ 
        pt: { xs: 10, sm: 12 }, 
        mb: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 },
      }}
    >
      <Typography 
        variant="h4" 
        component="h1" 
        sx={{ 
          fontSize: { xs: '1.5rem', sm: '2rem' },
          textAlign: 'center',
          mb: { xs: 2, sm: 3 },
        }}
      >
        Photo Gallery
      </Typography>
      <Typography 
        variant="body1" 
        color="text.secondary" 
        sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}
      >
        {galleryPhotos.length === 0
          ? 'No photos yet. Upload your first photo!'
          : `Viewing ${galleryPhotos.length} photo${galleryPhotos.length !== 1 ? 's' : ''}`}
      </Typography>

      {galleryPhotos.length > 0 && (
        <Box>
          {sortedMonths.map((yearMonth, monthIndex) => (
            <Box key={yearMonth} sx={{ mb: monthIndex < sortedMonths.length - 1 ? 4 : 0 }}>
              {/* Month Separator */}
              <Box sx={{ mb: 2, mt: monthIndex > 0 ? 3 : 0 }}>
                <Divider sx={{ mb: 1 }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    textAlign: 'center',
                  }}
                >
                  {getMonthName(yearMonth)}
                </Typography>
                <Divider sx={{ mt: 1 }} />
              </Box>

              {/* Photos for this month */}
              <ImageList 
                cols={isSmallScreen ? 1 : isMediumScreen ? 2 : 3} 
                gap={isSmallScreen ? 8 : 16}
              >
                {groupedPhotos[yearMonth].map((photo) => (
                  <ImageListItem
                    key={photo.id}
                    sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                    }}
                    onClick={() => handlePhotoClick(photo)}
                  >
                    <img
                      src={getPhotoUrl(photo)}
                      alt={`Photo from ${photo.date}`}
                      loading="lazy"
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 1.5, 
                        width: '100%',
                        bgcolor: 'background.paper',
                        borderRadius: 0,
                      }}
                    >
                      {photo.description && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.75rem',
                            color: 'text.primary',
                            mb: photo.rating ? 0.75 : 0.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {photo.description}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                        {photo.rating && (
                          <Chip
                            label={getRatingLabel(photo.rating)}
                            size="small"
                            sx={{
                              bgcolor: getRatingColor(photo.rating),
                              color: '#fff',
                              fontSize: '0.7rem',
                              height: 20,
                              '& .MuiChip-label': {
                                px: 1,
                              },
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.65rem',
                        }}
                      >
                        {formatDateShort(photo.date)}
                      </Typography>
                    </Paper>
                  </ImageListItem>
                ))}
              </ImageList>
            </Box>
          ))}
        </Box>
      )}
    </Container>
  );
};

export default GalleryPage;

